import { WaitStatus } from '#/components/common/loading-state'
import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { ArrowUp, Globe, History, Loader2, Paperclip } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '#/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '#/components/ui/select'
import { Tooltip, TooltipContent, TooltipTrigger } from '#/components/ui/tooltip'
import { api, ApiError } from '#/lib/api'
import { ATTACHMENT_ACCEPT, ATTACHMENT_MAX_PER_PLAN, uploadAttachment } from '#/lib/api-gen'
import { acceptAttachmentFiles, AttachmentChip, mergeFiles, uploadErrorText } from '#/components/common/attachments'
import { useHydratedSession } from '#/lib/auth-client'
import { STARTER_KITS, type StarterKit } from '#/lib/templates'
import type { TechStack } from '#/lib/types'
import { m } from '#/paraglide/messages'
import { getLocale } from '#/paraglide/runtime'
import { PlanDrawer } from './plan-drawer'
import { ModelPicker } from '#/components/common/model-picker'
import type { ModelId } from '#/lib/ai/models'
import { kitTechStack, StarterKits } from './starter-kits'
import { SimpleKits } from '#/components/simple/simple-kits'
import { useBuilderMode } from '#/lib/simple-mode'

const DRAFT_KEY = 'bystep:idea-draft'
const MIN = 20

interface Draft {
  idea: string
  language: 'en' | 'id'
  planId?: string
  /** starter kit the idea came from, and the stack it suggests for step 2 (moat plan §6.2) */
  kitId?: string | null
  tech?: TechStack | null
  /** model selector choice made on step 1 (model id / 'auto'; null = user default) */
  model?: ModelId | null
}

/** `initialKitId` (Parity P5): a playbook/kit chosen on a public page — applied once after mount, never over a draft that already became a plan. */
export function IdeaForm({ initialKitId = null }: { initialKitId?: string | null } = {}) {
  const navigate = useNavigate()
  const { data: session } = useHydratedSession()
  // non-tech F0: simple mode hides the stack/model choices and shows the WhatsApp-first kits
  const { simple } = useBuilderMode()
  const [idea, setIdea] = useState('')
  const [language, setLanguage] = useState<'en' | 'id'>(getLocale() === 'id' ? 'id' : 'en')
  const [busy, setBusy] = useState(false)
  const [drawer, setDrawer] = useState(false)
  const [kitId, setKitId] = useState<string | null>(null)
  const [tech, setTech] = useState<TechStack | null>(null)
  const [model, setModel] = useState<ModelId | null>(null)
  const ref = useRef<HTMLTextAreaElement>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const [files, setFiles] = useState<File[]>([])
  const [uploading, setUploading] = useState<{ done: number; total: number } | null>(null)

  const addFiles = (list: FileList | File[] | null) => {
    const next = acceptAttachmentFiles(list)
    if (next.length) setFiles((prev) => mergeFiles(prev, next).slice(0, ATTACHMENT_MAX_PER_PLAN))
    if (fileRef.current) fileRef.current.value = ''
  }

  // restore draft; if it already has a planId, resume at step 2
  useEffect(() => {
    try {
      const d = JSON.parse(localStorage.getItem(DRAFT_KEY) ?? 'null') as Draft | null
      if (d?.planId) {
        navigate({ to: '/plans/$planId', params: { planId: d.planId }, search: { step: 2 } })
        return
      }
      if (d) {
        setIdea(d.idea ?? '')
        if (d.language) setLanguage(d.language)
        setKitId(d.kitId ?? null)
        setTech(d.tech ?? null)
        setModel(d.model ?? null)
      }
    } catch {}
  }, [navigate])
  useEffect(() => {
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ idea, language, kitId, tech, model } satisfies Draft))
    } catch {}
  }, [idea, language, kitId, tech, model])
  // auto-grow
  useEffect(() => {
    const el = ref.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(280, el.scrollHeight)}px`
  }, [idea])

  const pickKit = (kit: StarterKit) => {
    setIdea(kit.idea[language])
    setKitId(kit.id)
    setTech(kitTechStack(kit))
    toast.success(m.fw_kits_applied({ title: kit.title[language] }))
    ref.current?.focus()
  }
  const appliedInitial = useRef(false)
  useEffect(() => {
    if (!initialKitId || appliedInitial.current) return
    const kit = STARTER_KITS.find((k) => k.id === initialKitId)
    if (!kit) return
    appliedInitial.current = true
    setIdea(kit.idea[language])
    setKitId(kit.id)
    setTech(kitTechStack(kit))
  }, [initialKitId, language])

  const submit = async () => {
    if (idea.trim().length < MIN || busy) return
    if (!session?.user) {
      navigate({ to: '/sign-in', search: { redirect: '/plan?flow=new' } })
      return
    }
    setBusy(true)
    try {
      const r = await api<{ planId: string }>('/api/plan/create', { method: 'POST', json: { idea: idea.trim(), language, model: simple ? null : model, ...(simple ? { summaryMode: 'simple' } : {}), ...(kitId ? { kitId } : {}) } })
      try {
        localStorage.setItem(DRAFT_KEY, JSON.stringify({ idea, language, kitId, tech, model, planId: r.planId }))
      } catch {}
      // a kit that names part of the stack pre-fills step 2; never worth failing the flow over
      if (tech && !simple) {
        try {
          await api(`/api/plan/${r.planId}`, { method: 'PATCH', json: { techStack: tech } })
        } catch {}
      }
      // spec/01 §4.1: upload sequentially; one failure stops the rest but the plan stays
      if (files.length) {
        for (let i = 0; i < files.length; i++) {
          const f = files[i]!
          setUploading({ done: i, total: files.length })
          try {
            await uploadAttachment(r.planId, f, 'plan')
          } catch (e) {
            toast.error(m.idea_attach_upload_failed({ name: f.name, reason: uploadErrorText(e, ATTACHMENT_MAX_PER_PLAN) }))
            setFiles((prev) => prev.slice(i))
            setUploading(null)
            setBusy(false)
            return
          }
        }
        setUploading(null)
      }
      // simple mode has no tech step — go straight to the three plain questions
      window.location.href = `/plans/${r.planId}?step=${simple ? 3 : 2}`
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : m.common_error_generic())
      setBusy(false)
    }
  }

  return (
    <div className="mx-auto w-full max-w-2xl space-y-6">
      <WaitStatus active={busy} label={uploading ? m.loading_upload_progress(uploading) : m.loading_working()} description={m.loading_questions_hint()} />
      <div className="space-y-2">
        <h1 className="text-3xl font-bold sm:text-4xl">{m.idea_title()}</h1>
        <p className="text-muted-foreground">
          {m.idea_subtitle_prefix()}
          <Link to="/plan" search={{ flow: 'existing' }} className="rounded-md border bg-muted px-1.5 py-0.5 font-mono text-xs text-foreground hover:border-primary">
            {'</>'} {m.idea_subtitle_link()}
          </Link>
        </p>
      </div>
      <div
        className="rounded-2xl border border-primary/50 bg-card p-3 shadow-sm focus-within:border-primary"
        onDragOver={(e) => { if (e.dataTransfer.types.includes('Files')) e.preventDefault() }}
        onDrop={(e) => { if (e.dataTransfer.files.length && !busy) { e.preventDefault(); addFiles(e.dataTransfer.files) } }}
        // a copy from Word/Excel carries a picture of the text too — only take files when there is no text
        onPaste={(e) => { if (e.clipboardData.files.length && !e.clipboardData.types.includes('text/plain') && !busy) { e.preventDefault(); addFiles(e.clipboardData.files) } }}
      >
        <textarea
          ref={ref}
          value={idea}
          onChange={(e) => setIdea(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              void submit()
            }
          }}
          rows={3}
          placeholder={m.idea_placeholder()}
          className="w-full resize-none bg-transparent px-1 text-base outline-none placeholder:text-muted-foreground/70"
        />
        {files.length ? (
          <ul className="mt-2 flex flex-wrap gap-1.5 px-1">
            {files.map((f) => (
              <AttachmentChip key={`${f.name}-${f.size}`} name={f.name} size={f.size} disabled={busy} onRemove={() => setFiles((prev) => prev.filter((x) => x !== f))} />
            ))}
            {uploading ? (
              <li className="flex items-center gap-1.5 px-1 text-xs text-muted-foreground">
                <Loader2 className="size-3.5 animate-spin" /> {m.idea_attach_uploading({ done: uploading.done, total: uploading.total })}
              </li>
            ) : null}
          </ul>
        ) : null}
        <div className="mt-2 flex items-center justify-between gap-2">
          <div className="flex items-center gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button type="button" variant="ghost" size="sm" className="h-8 gap-1.5 text-xs" disabled={busy} onClick={() => fileRef.current?.click()}>
                    <Paperclip className="size-4" /> {m.idea_attach()}
                    {files.length ? <span className="rounded-full bg-primary/10 px-1.5 text-[10px] font-semibold text-primary">{files.length}</span> : null}
                  </Button>
                  <input ref={fileRef} type="file" multiple accept={ATTACHMENT_ACCEPT} className="hidden" onChange={(e) => addFiles(e.target.files)} />
                </span>
              </TooltipTrigger>
              <TooltipContent>{m.idea_attach_hint()}</TooltipContent>
            </Tooltip>
            <Select value={language} onValueChange={(v) => setLanguage(v as 'en' | 'id')}>
              <SelectTrigger size="sm" className="h-8 gap-1.5 border-0 bg-transparent text-xs shadow-none" aria-label={m.idea_language()}>
                <Globe className="size-4" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="id">{m.idea_language_id()}</SelectItem>
                <SelectItem value="en">{m.idea_language_en()}</SelectItem>
              </SelectContent>
            </Select>
            {simple ? null : <ModelPicker value={model} onChange={setModel} allowDefault className="h-8 border-0 bg-transparent text-xs shadow-none" />}
          </div>
          <div className="flex items-center gap-2">
            {idea.trim().length > 0 && idea.trim().length < MIN ? <span className="text-xs text-muted-foreground">{m.idea_min_length({ min: MIN })}</span> : null}
            <Button type="button" size="icon" className="size-9 rounded-full" disabled={idea.trim().length < MIN || busy} onClick={submit} aria-label={m.idea_submit()}>
              <ArrowUp className="size-4" />
            </Button>
          </div>
        </div>
      </div>
      {simple ? (
        <SimpleKits locale={language} selectedId={kitId} onPick={pickKit} onOwnIdea={() => { setKitId(null); setIdea(''); ref.current?.focus() }} />
      ) : (
        <StarterKits locale={language} selectedId={kitId} onPick={pickKit} />
      )}
      <div className="text-center">
        <button className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" onClick={() => setDrawer(true)}>
          <History className="size-4" /> {m.idea_previous()}
        </button>
      </div>
      <PlanDrawer open={drawer} onOpenChange={setDrawer} />
    </div>
  )
}

export function clearIdeaDraft() {
  try {
    localStorage.removeItem(DRAFT_KEY)
  } catch {}
}
